import { Pathname } from 'Routes';
import useMyAxios from 'hooks/useMyAxios';
import { getAllActivePlans } from 'Api/apiFunctions';
import { savePostPurchaseReturn } from 'Utils/postPurchaseReturn';
import {
  TRIAL_FILE_CAP_ENABLED, TRIAL_MAX_FILES, TRIAL_MAX_GB,
  isTrialFileCapEnabled, getTrialMaxFiles, getTrialMaxGB,
} from 'Commons';
import { navigateTo } from '../../navigationHelper';

// Shared data/behaviour for the upload-blocker (upgrade) popup, used by both the
// classic and new-UI variants so they stay identical apart from presentation.
export const UPLOAD_BLOCKER_FEATURES = [
  'Unlimited Uploads',
  'Unlimited Cloud Storage',
  'Unlimited Sharing',
  'Tell Your Story',
  'Import from phone, Dropbox & Google Drive',
];

// Copy variants so the same popup covers both "no plan → start a trial" and
// "trial account hit its allowance → upgrade". Presentation is identical; only
// the words and CTAs change. The limit copy derives from the Commons trial
// constants, so it always reflects what's actually enforced (storage-only today;
// flip TRIAL_FILE_CAP_ENABLED and it reads "N files or N GB" automatically).
export const UPLOAD_BLOCKER_COPY = {
  default: {
    badge: '★ Free trial',
    badgeShort: 'Free trial',
    eyebrow: 'Free trial',
    title: 'Start your free trial',
    subtitle:
      'Start your free trial to upload and keep all your videos — unlimited uploads and storage, nothing to lose.',
    primaryCta: 'Start your 14-day Free Trial',
    secondaryCta: 'Continue as viewer',
    showPrice: true,
  },
  limit: {
    badge: '★ Trial limit',
    badgeShort: 'Trial limit',
    eyebrow: 'Trial limit reached',
    title: "You've reached your trial limit",
    subtitle: TRIAL_FILE_CAP_ENABLED
      ? `Your free trial covers up to ${TRIAL_MAX_FILES} files or ${TRIAL_MAX_GB} GB of storage. Upgrade to a paid plan for unlimited uploads and storage.`
      : `Your free trial covers up to ${TRIAL_MAX_GB} GB of storage. Upgrade to a paid plan for unlimited uploads and storage.`,
    primaryCta: 'Upgrade',
    // Closing the popup lets the user re-pick a smaller selection within the cap.
    secondaryCta: TRIAL_FILE_CAP_ENABLED ? `Choose up to ${TRIAL_MAX_FILES} files` : 'Choose smaller files',
    // No price here — the amount depends on the plan the user picks on billing.
    showPrice: false,
  },
};

// Where the primary CTA sends the user, per variant. The limit popup (a user who
// is already on a trial) goes to the billing page to upgrade; the default blocker
// (a free / no-plan user) goes to the plans page to start a trial.
const PRIMARY_DESTINATION = {
  default: Pathname.plans,
  limit: `${Pathname.settings.basePath}${Pathname.settings.billings}`,
};

export const useUploadBlocker = (onHide, variant = 'default') => {
  const base = UPLOAD_BLOCKER_COPY[variant] || UPLOAD_BLOCKER_COPY.default;
  // The trial-limit copy is rebuilt from the live getConfig allowance so it
  // always matches what's actually enforced (storage/count can change server-side).
  const copy = variant === 'limit'
    ? {
      ...base,
      subtitle: isTrialFileCapEnabled()
        ? `Your free trial covers up to ${getTrialMaxFiles()} files or ${getTrialMaxGB()} GB of storage. Upgrade to a paid plan for unlimited uploads and storage.`
        : `Your free trial covers up to ${getTrialMaxGB()} GB of storage. Upgrade to a paid plan for unlimited uploads and storage.`,
      secondaryCta: isTrialFileCapEnabled() ? `Choose up to ${getTrialMaxFiles()} files` : 'Choose smaller files',
    }
    : base;
  // Cached under the same key used elsewhere, so this rarely refetches.
  const { response: plans } = useMyAxios(getAllActivePlans, null, 'ALL_ACTIVE_PLANS');
  const yearly = plans?.data?.find((p) => p?.id === '11');
  const amount = yearly?.amount;
  const priceText = amount ? `$${amount}` : '';

  // Uses navigateTo (not the useNavigate hook) because the blocker is rendered
  // globally in app.jsx OUTSIDE the <Router>, where router hooks would throw.
  const goToPlans = () => {
    // Remember the current page so the trial/purchase flow can bring the user
    // right back here to continue uploading (instead of the default dashboard).
    savePostPurchaseReturn(window.location.pathname + window.location.search);
    navigateTo(PRIMARY_DESTINATION[variant] || PRIMARY_DESTINATION.default);
    if (onHide) onHide();
  };

  return { priceText, goToPlans, copy };
};
